/**
 * constants.js - Constantes globales de l'application
 *
 * VERSION 2.0.0 - Architecture data[] unifiée
 * ===========================================
 *
 * Constantes et utilitaires pour la gestion des projets de fibres optiques
 * Support complet du nouveau format basé sur data[] uniquement
 *
 * @author ITE Technologies
 * @version 2.0.0
 * @since 2025-01-17
 */

// ===== CONSTANTES FIBRES OPTIQUES =====

/**
 * Ordre des statuts pour la rotation cyclique
 * Utilisé par les boutons de changement de statut
 */
export const STATUS_ORDER = ["N/A", "LIBRE", "OCCUPÉE"];

/**
 * Couleurs associées aux statuts des fibres
 * Utilisées pour l'affichage visuel dans l'interface
 */
export const STATUS_COLORS = {
  "N/A": "#d3d3d3", // Gris clair
  LIBRE: "limegreen", // Vert
  OCCUPÉE: "gold", // Jaune/Orange
};

/**
 * Images associées aux statuts des fibres
 * Fichiers PNG dans le dossier assets/
 */
export const STATUS_IMAGES = {
  "N/A": "fiber_na.png",
  LIBRE: "fiber_libre.png",
  OCCUPÉE: "fiber_occupee.png",
};

// ===== RECONNAISSANCE VOCALE =====

/**
 * Commandes vocales reconnues pour chaque statut
 * Supportent les variations de prononciation française
 */
export const SPEECH_COMMANDS = {
  Libre: ["libre", "livre", "disponible", "vert", "vers"],
  Occupée: ["pris", "occupé", "connecté", "jaune"],
  "N/A": ["vide", "nok", "gris"],
};

/**
 * Configuration de la reconnaissance vocale
 * Optimisée pour les commandes en français
 */
export const SPEECH_CONFIG = {
  language: "fr-FR",
  continuous: true,
  interimResults: false,
  timeout: 30000, // 30 secondes avant arrêt automatique
};

// ===== CONFIGURATION DES PROJETS =====

/**
 * Paramètres globaux des projets
 */
export const PROJECT_CONFIG = {
  fiberTypes: [12, 24], // Types de fibres supportés
  maxTiroirs: 6, // Nombre maximum de tiroirs
  defaultName: "PMXXX", // Nom par défaut
  defaultFiberType: 24, // Type de fibre par défaut
  defaultActiveTiroirs: [1, 2, 3, 4], // Tiroirs actifs par défaut
};

/**
 * Configuration des modules selon le type de fibres
 * Détermine le nombre de modules et leurs lettres
 */
export const MODULE_CONFIG = {
  24: {
    count: 6,
    letters: ["A", "B", "C", "D", "E", "F"],
    fibersPerModule: 24,
    totalPerTiroir: 144, // 6 modules × 24 fibres
    description: "6 modules de 24 fibres",
  },
  12: {
    count: 12,
    letters: ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"],
    fibersPerModule: 12,
    totalPerTiroir: 144, // 12 modules × 12 fibres
    description: "12 modules de 12 fibres",
  },
};

// ===== ÉTATS DE L'APPLICATION =====

/**
 * États possibles de l'application principale
 * Utilisés pour la navigation entre écrans
 */
export const APP_STATES = {
  LOADING: "loading",
  WELCOME: "welcome",
  PROJECT_CREATOR: "project_creator",
  MAIN_APP: "main_app",
};

/**
 * Types d'événements personnalisés pour la communication inter-composants
 * Permet un découplage entre les différents modules
 */
export const CUSTOM_EVENTS = {
  // Événements de projet
  PROJECT_CREATED: "projectCreated",
  PROJECT_LOADED: "projectLoaded",

  // Événements de modification
  TIROIR_TOGGLED: "tiroirToggled",
  MODULE_TOGGLED: "moduleToggled",
  DATA_CHANGED: "dataChanged",

  // Événements d'interface
  MODULE_EDIT_REQUESTED: "moduleEditRequested",
  CLOSE_RIBAN_REQUESTED: "closeRibanRequested",

  // Événements de statistiques
  TIROIR_STATS_CHANGED: "tiroirStatsChanged",
  REQUEST_TIROIR_STATS: "requestTiroirStats",
};

// ===== STOCKAGE LOCAL =====

/**
 * Clés de stockage localStorage
 * Version 2.0 avec structure unifiée
 */
export const STORAGE_KEYS = {
  currentProject: "tiroirs_conf_current_project_v2",
  projectsList: "tiroirs_conf_projects_list_v2",
  userPreferences: "tiroirs_conf_preferences",
};

// ===== VALIDATION ET CONTRÔLES =====

/**
 * Limites et contraintes de validation
 */
export const VALIDATION_LIMITS = {
  projectNameMinLength: 1,
  projectNameMaxLength: 50,
  maxFibersPerModule: 24,
  minFibersPerModule: 12,
  maxTiroirs: 6,
  minTiroirs: 1,
  maxModulesPerTiroir: 12,
};

/**
 * Messages d'erreur standardisés
 */
export const ERROR_MESSAGES = {
  PROJECT_NAME_REQUIRED: "Le nom du projet est requis",
  PROJECT_NAME_TOO_LONG: "Le nom du projet est trop long (max 50 caractères)",
  INVALID_FIBER_TYPE: "Type de fibres invalide (12 ou 24 uniquement)",
  NO_ACTIVE_TIROIRS: "Au moins un tiroir doit être activé",
  INVALID_TIROIR_NUMBER: "Numéros de tiroirs invalides (1-4)",
  INVALID_MODULE_LETTER: "Lettre de module invalide",
  INVALID_PORT_NUMBER: "Numéro de port invalide",
  INVALID_STATUS: "Statut de fibre invalide",
  STORAGE_ERROR: "Erreur de sauvegarde des données",
  IMPORT_FORMAT_ERROR: "Format de fichier d'import invalide",
  EXPORT_ERROR: "Erreur lors de l'export des données",
};

// ===== UTILITAIRES PROJET =====

/**
 * Obtenir les lettres des modules selon le type de fibres
 *
 * @param {number} fiberType - Type de fibres (12 ou 24)
 * @returns {Array<string>} Lettres des modules disponibles
 */
export function getModuleLetters(fiberType) {
  const config = MODULE_CONFIG[fiberType];
  if (!config) {
    console.warn(`⚠️ Type de fibre invalide: ${fiberType}, utilisation de 24 par défaut`);
    return MODULE_CONFIG[24].letters;
  }
  return [...config.letters];
}

/**
 * Obtenir le nombre de modules selon le type de fibres
 *
 * @param {number} fiberType - Type de fibres (12 ou 24)
 * @returns {number} Nombre de modules
 */
export function getModuleCount(fiberType) {
  const config = MODULE_CONFIG[fiberType];
  return config?.count || MODULE_CONFIG[24].count;
}

/**
 * Obtenir la configuration complète d'un type de fibres
 *
 * @param {number} fiberType - Type de fibres (12 ou 24)
 * @returns {Object} Configuration du type de fibres
 */
export function getFiberTypeConfig(fiberType) {
  const config = MODULE_CONFIG[fiberType];
  if (!config) {
    console.warn(`⚠️ Type de fibre invalide: ${fiberType}, utilisation de 24 par défaut`);
    return MODULE_CONFIG[24];
  }
  return { ...config };
}

/**
 * Calculer le nombre total de fibres d'un projet
 *
 * @param {Object} project - Configuration du projet
 * @returns {number} Nombre total de fibres possibles
 */
export function calculateProjectTotalFibers(project) {
  if (!project || !project.activeTiroirs) {
    return 0;
  }

  const activeTiroirCount = project.activeTiroirs.length;
  const config = getFiberTypeConfig(project.fiberType);

  return activeTiroirCount * config.totalPerTiroir;
}

/**
 * Calculer le nombre de fibres actives d'un projet
 *
 * @param {Object} project - Configuration du projet avec data[]
 * @returns {number} Nombre de fibres configurées
 */
export function calculateProjectActiveFibers(project) {
  if (!project || !project.activeTiroirs || !project.activeModules) {
    return 0;
  }

  let totalActive = 0;

  project.activeTiroirs.forEach((tiroirNum) => {
    const tiroirKey = `T${tiroirNum}`;
    const activeModules = project.activeModules[tiroirKey] || [];
    totalActive += activeModules.length * project.fiberType;
  });

  return totalActive;
}

/**
 * Calculer le nombre de fibres configurées dans un projet
 *
 * @param {Array<Object>} data - Données du projet (format data[])
 * @returns {Object} Statistiques de configuration
 */
export function calculateProjectStats(data) {
  if (!Array.isArray(data)) {
    return { total: 0, configured: 0, libres: 0, occupees: 0, percentage: 0 };
  }

  const configured = data.length;
  const libres = data.filter((row) => row.statut === "LIBRE").length;
  const occupees = data.filter((row) => row.statut === "OCCUPÉE").length;

  return {
    total: configured,
    configured,
    libres,
    occupees,
    percentage: configured > 0 ? 100 : 0, // Si on a des données, c'est 100% configuré
  };
}

// ===== GÉNÉRATION ET FORMATAGE =====

/**
 * Générer le nom de fichier d'export avec timestamp
 *
 * @param {string} projectName - Nom du projet
 * @returns {string} Nom de fichier sécurisé avec timestamp
 */
export function generateExportFilename(projectName) {
  const now = new Date();
  const timestamp = now.toISOString().replace(/[:.]/g, "-").slice(0, 19).replace("T", "_");

  // Nettoyer le nom du projet pour le système de fichiers
  const safeName = projectName
    .replace(/[^a-zA-Z0-9\-_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .substring(0, 30); // Limiter la longueur

  return `${safeName}_${timestamp}.json`;
}

/**
 * Formater un nom de module pour l'affichage
 *
 * @param {string} tiroir - Identifiant du tiroir (ex: "T1")
 * @param {string} module - Lettre du module (ex: "A")
 * @returns {string} Nom formaté pour l'affichage
 */
export function formatModuleName(tiroir, module) {
  return `TIROIR : ${tiroir} - MODULE ${module}`;
}

/**
 * Parser un nom de module formaté
 *
 * @param {string} formattedName - Nom formaté (ex: "TIROIR : T1 - MODULE A")
 * @returns {Object|null} Objet avec tiroir et module ou null si invalide
 */
export function parseModuleName(formattedName) {
  const match = formattedName.match(/TIROIR\s*:\s*(T\d+)\s*-\s*MODULE\s*([A-L])/i);
  if (!match) {
    return null;
  }

  return {
    tiroir: match[1],
    module: match[2].toUpperCase(),
  };
}

// ===== VALIDATION =====

/**
 * Valider une configuration de projet avant création
 *
 * @param {Object} config - Configuration à valider
 * @returns {Object} Résultat de la validation avec erreurs
 */
export function validateProjectConfig(config) {
  const errors = [];

  // Validation du nom
  if (!config.name || typeof config.name !== "string") {
    errors.push(ERROR_MESSAGES.PROJECT_NAME_REQUIRED);
  } else {
    const trimmedName = config.name.trim();
    if (trimmedName.length < VALIDATION_LIMITS.projectNameMinLength) {
      errors.push(ERROR_MESSAGES.PROJECT_NAME_REQUIRED);
    }
    if (trimmedName.length > VALIDATION_LIMITS.projectNameMaxLength) {
      errors.push(ERROR_MESSAGES.PROJECT_NAME_TOO_LONG);
    }
  }

  // Validation du type de fibres
  if (!PROJECT_CONFIG.fiberTypes.includes(config.fiberType)) {
    errors.push(ERROR_MESSAGES.INVALID_FIBER_TYPE);
  }

  // Validation des tiroirs actifs
  if (!config.activeTiroirs || !Array.isArray(config.activeTiroirs) || config.activeTiroirs.length === 0) {
    errors.push(ERROR_MESSAGES.NO_ACTIVE_TIROIRS);
  } else {
    const invalidTiroirs = config.activeTiroirs.filter(
      (t) => !Number.isInteger(t) || t < 1 || t > PROJECT_CONFIG.maxTiroirs
    );
    if (invalidTiroirs.length > 0) {
      errors.push(ERROR_MESSAGES.INVALID_TIROIR_NUMBER);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: [], // Pour futures validations non-bloquantes
  };
}

/**
 * Valider un statut de fibre
 *
 * @param {string} status - Statut à valider
 * @returns {boolean} Validité du statut
 */
export function validateFiberStatus(status) {
  return STATUS_ORDER.includes(status);
}

/**
 * Valider un numéro de tiroir
 *
 * @param {number} tiroirNum - Numéro à valider
 * @returns {boolean} Validité du numéro
 */
export function validateTiroirNumber(tiroirNum) {
  return Number.isInteger(tiroirNum) && tiroirNum >= 1 && tiroirNum <= PROJECT_CONFIG.maxTiroirs;
}

/**
 * Valider une lettre de module
 *
 * @param {string} moduleLetter - Lettre à valider
 * @param {number} fiberType - Type de fibres pour déterminer les lettres valides
 * @returns {boolean} Validité de la lettre
 */
export function validateModuleLetter(moduleLetter, fiberType = 24) {
  const validLetters = getModuleLetters(fiberType);
  return validLetters.includes(moduleLetter);
}

/**
 * Valider un numéro de port
 *
 * @param {number} port - Numéro de port à valider
 * @param {number} fiberType - Type de fibres (12 ou 24)
 * @returns {boolean} Validité du port
 */
export function validatePortNumber(port, fiberType = 24) {
  return Number.isInteger(port) && port >= 1 && port <= fiberType;
}

// ===== CRÉATION DE PROJETS =====

/**
 * Créer un projet par défaut avec tous les paramètres
 *
 * @param {Object} customConfig - Configuration personnalisée (optionnelle)
 * @returns {Object} Projet configuré et prêt à utiliser
 */
export function createDefaultProject(customConfig = {}) {
  const config = {
    name: PROJECT_CONFIG.defaultName,
    fiberType: PROJECT_CONFIG.defaultFiberType,
    activeTiroirs: [...PROJECT_CONFIG.defaultActiveTiroirs],
    ...customConfig,
  };

  return {
    name: config.name,
    fiberType: config.fiberType,
    activeTiroirs: [...config.activeTiroirs],
    activeModules: generateDefaultActiveModules(config.fiberType, config.activeTiroirs),
    createdAt: new Date().toISOString(),
    lastModified: new Date().toISOString(),
    data: [], // Format unifié vide
  };
}

/**
 * Générer les modules actifs par défaut selon la configuration
 *
 * @param {number} fiberType - Type de fibres (12 ou 24)
 * @param {Array<number>} activeTiroirs - Tiroirs actifs
 * @returns {Object} Modules actifs par tiroir
 */
function generateDefaultActiveModules(fiberType = 24, activeTiroirs = []) {
  const activeModules = {};
  const moduleLetters = getModuleLetters(fiberType);

  activeTiroirs.forEach((tiroirNum) => {
    const tiroirKey = `T${tiroirNum}`;
    activeModules[tiroirKey] = [...moduleLetters];
  });

  return activeModules;
}

// ===== HELPERS DIVERS =====

/**
 * Normaliser un statut pour le stockage
 *
 * @param {string} status - Statut d'entrée
 * @returns {string} Statut normalisé
 */
export function normalizeStatus(status) {
  if (!status) return "N/A";

  const normalized = status.toString().toUpperCase().trim();

  switch (normalized) {
    case "LIBRE":
    case "VERT":
    case "DISPONIBLE":
      return "LIBRE";
    case "OCCUPÉE":
    case "OCCUPEE":
    case "PRIS":
    case "JAUNE":
      return "OCCUPÉE";
    case "N/A":
    case "NA":
    case "VIDE":
    case "GRIS":
      return "N/A";
    default:
      return "N/A";
  }
}

/**
 * Obtenir la couleur d'un statut
 *
 * @param {string} status - Statut de la fibre
 * @returns {string} Couleur CSS associée
 */
export function getStatusColor(status) {
  const normalizedStatus = normalizeStatus(status);
  return STATUS_COLORS[normalizedStatus] || STATUS_COLORS["N/A"];
}

/**
 * Obtenir l'image d'un statut
 *
 * @param {string} status - Statut de la fibre
 * @returns {string} Nom du fichier image
 */
export function getStatusImage(status) {
  const normalizedStatus = normalizeStatus(status);
  return STATUS_IMAGES[normalizedStatus] || STATUS_IMAGES["N/A"];
}
